import useStory, { span } from './useStory';
import { INSIDE } from '../../data/guideDifference';
import { Explainer } from './GuideParts';

// WHAT IS DIFFERENT INSIDE (guide 3, rebuilt 20 Oct 2026): a cut-away of each
// kind's outdoor unit. The story lights up the non-inverter's parts, then the
// inverter's, and rests with everything shown. The compressor turns the way
// each kind runs: on and off at one speed, or steadily slower. An
// illustration, not to scale.
//
// Props: watch ({ title, captions }), sources.

const SPOTS = { compressor: [255, 140], board: [255, 50] };

function Cutaway({ kind, t, lit }) {
  const inverter = kind === 'inverter';
  const time = t * 18;
  const on = inverter || time % 5 < 3 || t >= 1;
  const a = inverter ? time * 300 : (Math.floor(time / 5) * 3 + Math.min(time % 5, 3)) * 700;
  const tone = inverter ? '#2563eb' : '#6b7280';
  return (
    <svg viewBox="0 0 320 200" width="100%" role="img" className="block h-auto"
      aria-label={`Inside a ${inverter ? 'inverter' : 'non-inverter'} outdoor unit: fan, compressor and ${inverter ? 'circuit board' : 'switch and capacitor'}`}>
      <rect x="10" y="10" width="300" height="180" rx="12" fill="#fff" stroke="#d1d5db" strokeWidth="1.5" />
      <circle cx="105" cy="100" r="74" fill="#f9fafb" stroke="#e5e7eb" />
      <g transform={`rotate(${(a * 0.4) % 360} 105 100)`} fill="#cbd5e1">
        <path d="M105 100c-6-26 2-44 12-46s9 20-4 40z" />
        <path d="M105 100c25 10 36 26 31 35s-22 0-31-27z" />
        <path d="M105 100c-20 18-40 18-44 9s15-17 36-16z" />
      </g>
      <circle cx="105" cy="100" r="7" fill="#9ca3af" />
      <line x1="196" x2="196" y1="18" y2="182" stroke="#e5e7eb" strokeWidth="1.5" />
      <rect x="210" y="24" width="90" height="54" rx="7" fill={inverter ? '#eff6ff' : '#f9fafb'} stroke={lit.board ? '#111827' : '#d1d5db'} strokeWidth={lit.board ? 2.5 : 1} />
      {inverter ? (
        <g>
          <rect x="222" y="34" width="66" height="34" rx="3" fill="#dbeafe" stroke="#93c5fd" />
          <rect x="244" y="42" width="20" height="18" rx="2" fill="#1d4ed8" />
          {[0, 1, 2, 3].map((i) => <rect key={i} x="227" y={38 + i * 7} width="10" height="3" rx="1" fill="#60a5fa" />)}
        </g>
      ) : (
        <g>
          <rect x="226" y="34" width="22" height="34" rx="9" fill="#e5e7eb" stroke="#6b7280" />
          <rect x="260" y="40" width="28" height="22" rx="3" fill="#fff" stroke="#d6d3d1" />
          <path d="M266 51h6l4-5" stroke="#78716c" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </g>
      )}
      <rect x="222" y="112" width="66" height="68" rx="20" fill="#f3f4f6" stroke={lit.compressor ? '#111827' : '#9ca3af'} strokeWidth={lit.compressor ? 2.5 : 1} />
      <circle cx="255" cy="142" r="19" fill="#fff" stroke="#d1d5db" />
      <g transform={`rotate(${a % 360} 255 142)`}>
        <circle cx="255" cy="142" r="12" fill="none" stroke={tone} strokeWidth="3" strokeDasharray="10 4" />
        <rect x="253" y="128" width="4" height="10" rx="2" fill={tone} />
      </g>
      <circle cx="280" cy="170" r="3.5" fill={on ? '#16a34a' : '#d1d5db'} />
      {INSIDE[kind].map((it, i) => {
        const [x, y] = SPOTS[it.part];
        const on2 = lit[it.part];
        return (
          <g key={it.part}>
            <circle cx={x - 40} cy={y} r="11" fill={on2 ? '#111827' : '#fff'} stroke="#111827" strokeWidth="1.5" />
            <text x={x - 40} y={y + 4} textAnchor="middle" fontSize="13" fontWeight="800" fill={on2 ? '#fff' : '#111827'}>{i + 1}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function InsideExplainer({ watch, sources }) {
  const story = useStory(18000);
  const t = story.t;
  const done = t >= 0.999;
  const litFor = (kind) => {
    const [a, b] = kind === 'fixed' ? [0.25, 0.55] : [0.55, 0.8];
    const k = span(t, a, b);
    return { compressor: done || k > 0, board: done || k > 0.5 };
  };
  return (
    <Explainer id="watch-inside" level={3} title={watch.title} captions={watch.captions} story={story} sources={sources}>
      <div className="grid gap-3 sm:grid-cols-2">
        {['fixed', 'inverter'].map((kind) => {
          const lit = litFor(kind);
          return (
            <div key={kind} className="grid gap-2 rounded-xl border border-gray-200 p-3">
              <p className="text-[15px] font-extrabold text-gray-900">{kind === 'fixed' ? 'Non-inverter' : 'Inverter'}</p>
              <Cutaway kind={kind} t={t} lit={lit} />
              <ol className="grid gap-1 text-[14px] text-gray-700">
                {INSIDE[kind].map((it, i) => (
                  <li key={it.part} className={lit[it.part] ? 'font-bold text-gray-900' : 'text-gray-500'}>{i + 1}. {it.name}</li>
                ))}
              </ol>
            </div>
          );
        })}
      </div>
      <p className="text-[13px] text-gray-500">A drawing, not to scale. Where each part sits varies by model.</p>
    </Explainer>
  );
}
